import React, { useCallback, useEffect, useMemo, useState } from 'react';
import isEmpty from 'lodash/isEmpty';
import { useDispatch, useSelector } from 'react-redux';
import { useHistory } from 'react-router-dom';
import usePortal from 'react-useportal';
import { FriendlyPopup, TrainingPopup } from '../../../../components/Popup';
import { getFriendlyList } from '../../../../redux/actions/Friendly';
import { getTrainingList } from '../../../../redux/actions/Training';
import { getLeaguesList } from '../../../../redux/actions/League';
import { homeGetHomeInitialData } from '../../../../redux/actions/Home';
import { showPopupWindow } from '../../../../redux/actions/Popups';
import { isLeaguesListLoaded } from '../../../../redux/selector/League';
import { getPopupByName } from '../../../../redux/selector/Popups';
import { friendlyTeams } from '../../../../redux/selector/Friendly';
import { trainingTeams } from '../../../../redux/selector/Training';
import { getInfoAboutDataHome } from '../../../../redux/selector/Home';
import { getMyCurrentTeam } from '../../../../redux/selector/User';
import { getPracticeBoostUntil } from '../../../../redux/selector/Team';
import { POPUPS } from '../../../../service/constants';
import { toastRender } from '../../../../service/toaster';
import { matchHref } from '../../../../service/liveMatch';
import {
  formatGoalProgress,
  parseDevelopmentPayload,
  collectTeamNameMap,
  resolveOfferLabels,
  inviteAcceptBlockReason,
  acceptErrorMessage,
} from '../../../../service/developmentOffers';
import {
  getFriendlyData,
  acceptFriendlyOffer,
  rejectFriendlyOffer,
  cancelFriendlyOffer,
} from '../../../../api/friendly';
import {
  getTrainingData,
  acceptTrainingOffer,
  rejectTrainingOffer,
  cancelTrainingOffer,
} from '../../../../api/training';
import {
  StyledPage,
  StyledColumn,
  StyledSection,
  StyledBody,
  StyledOverview,
  StyledActionButton,
  StyledSubTitle,
  StyledList,
  StyledListItem,
  StyledPastMatchButton,
  StyledOfferActions,
  StyledOfferButton,
  StyledMutedNote,
  StyledOfferMeta,
  StyledOfferRow,
  StyledClubCell,
  StyledCrest,
  StyledCrestFallback,
  StyledClubName,
  StyledMatchup,
  StyledMatchSide,
  StyledMatchName,
  StyledMatchScore,
  StyledMatchDone,
  StyledGoalRow,
  StyledGoalLabel,
  StyledGoalValue,
  StyledEmptyRow,
} from './styled';

const Crest = ({ src, name }) =>
  src ? (
    <StyledCrest src={src} alt="" />
  ) : (
    <StyledCrestFallback aria-hidden>
      {(name || '?').charAt(0)}
    </StyledCrestFallback>
  );

const GoalsBlock = ({ daily, season, loading }) => (
  <>
    <StyledSubTitle>Goals</StyledSubTitle>
    {loading ? (
      <StyledMutedNote>Loading…</StyledMutedNote>
    ) : (
      <StyledList>
        <StyledGoalRow>
          <StyledGoalLabel>Daily</StyledGoalLabel>
          <StyledGoalValue>{formatGoalProgress(daily)}</StyledGoalValue>
        </StyledGoalRow>
        <StyledGoalRow>
          <StyledGoalLabel>Season</StyledGoalLabel>
          <StyledGoalValue>{formatGoalProgress(season)}</StyledGoalValue>
        </StyledGoalRow>
      </StyledList>
    )}
  </>
);

const PastMatchRow = ({ match }) => {
  const history = useHistory();
  const path = match.matchId ? matchHref(match.matchId) : null;
  const content = (
    <StyledMatchup>
      <StyledMatchSide>
        <Crest src={match.homeCrest} name={match.homeName} />
        <StyledMatchName title={match.homeName}>{match.homeName}</StyledMatchName>
      </StyledMatchSide>
      {match.value === 'Done' || match.value === '—' ? (
        <StyledMatchDone>{match.value}</StyledMatchDone>
      ) : (
        <StyledMatchScore>{match.value}</StyledMatchScore>
      )}
      <StyledMatchSide>
        <Crest src={match.awayCrest} name={match.awayName} />
        <StyledMatchName title={match.awayName}>{match.awayName}</StyledMatchName>
      </StyledMatchSide>
    </StyledMatchup>
  );
  if (!path) {
    return <StyledListItem>{content}</StyledListItem>;
  }
  return (
    <StyledListItem $flush>
      <StyledPastMatchButton
        type="button"
        onClick={() => history.push(path)}
        title="Open match report"
      >
        {content}
      </StyledPastMatchButton>
    </StyledListItem>
  );
};

const PastMatchesBlock = ({ title, pastMatches, loading }) => {
  if (loading) {
    return (
      <>
        <StyledSubTitle>{title}</StyledSubTitle>
        <StyledMutedNote>Loading…</StyledMutedNote>
      </>
    );
  }
  if (!pastMatches?.available) {
    return (
      <>
        <StyledSubTitle>{title}</StyledSubTitle>
        <StyledEmptyRow>History unavailable</StyledEmptyRow>
      </>
    );
  }
  return (
    <>
      <StyledSubTitle>{title}</StyledSubTitle>
      <StyledList>
        {pastMatches.items.length === 0 ? (
          <StyledEmptyRow>None yet</StyledEmptyRow>
        ) : (
          pastMatches.items.map((match) => (
            <PastMatchRow key={match.id} match={match} />
          ))
        )}
      </StyledList>
    </>
  );
};

const OffersBlock = ({
  title,
  offers,
  loading,
  globalBlockReason,
  offerErrors,
  onAccept,
  onReject,
  onCancel,
  busyId,
  kind,
  matchRunning,
  nextMatch,
  timezone,
}) => {
  if (loading) {
    return (
      <>
        <StyledSubTitle>{title}</StyledSubTitle>
        <StyledMutedNote>Loading…</StyledMutedNote>
      </>
    );
  }
  if (!offers?.available) {
    return (
      <>
        <StyledSubTitle>{title}</StyledSubTitle>
        <StyledEmptyRow>Inbox unavailable</StyledEmptyRow>
      </>
    );
  }
  return (
    <>
      <StyledSubTitle>Sent</StyledSubTitle>
      <StyledList>
        {offers.sent.length === 0 ? (
          <StyledEmptyRow>None</StyledEmptyRow>
        ) : (
          offers.sent.map((offer) => (
            <StyledListItem key={offer.id}>
              <StyledOfferRow>
                <StyledClubCell>
                  <Crest src={offer.crest} name={offer.label} />
                  <StyledClubName title={offer.label}>{offer.label}</StyledClubName>
                </StyledClubCell>
                <StyledOfferActions>
                  <StyledOfferButton
                    type="button"
                    disabled={busyId === offer.id}
                    onClick={() => onCancel(offer.id)}
                  >
                    Cancel
                  </StyledOfferButton>
                </StyledOfferActions>
              </StyledOfferRow>
            </StyledListItem>
          ))
        )}
      </StyledList>
      <StyledSubTitle>Received</StyledSubTitle>
      {globalBlockReason ? (
        <StyledMutedNote>{globalBlockReason}</StyledMutedNote>
      ) : null}
      <StyledList>
        {offers.received.length === 0 ? (
          <StyledEmptyRow>None</StyledEmptyRow>
        ) : (
          offers.received.map((offer) => {
            const rowBlock = inviteAcceptBlockReason({
              kind,
              matchRunning,
              nextMatch,
              timezone,
              canAccept: offer.canAccept,
            });
            const rowError = offerErrors[offer.id] || rowBlock;
            const blocked = Boolean(rowError);
            return (
              <StyledListItem key={offer.id}>
                <StyledOfferRow>
                  <StyledClubCell>
                    <Crest src={offer.crest} name={offer.label} />
                    <div style={{ minWidth: 0 }}>
                      <StyledClubName title={offer.label}>
                        {offer.label}
                      </StyledClubName>
                      {rowError ? (
                        <StyledOfferMeta>{rowError}</StyledOfferMeta>
                      ) : null}
                    </div>
                  </StyledClubCell>
                  <StyledOfferActions>
                    <StyledOfferButton
                      type="button"
                      $primary
                      disabled={blocked || busyId === offer.id}
                      title={rowError || 'Accept'}
                      onClick={() => onAccept(offer.id)}
                    >
                      Accept
                    </StyledOfferButton>
                    <StyledOfferButton
                      type="button"
                      disabled={busyId === offer.id}
                      onClick={() => onReject(offer.id)}
                    >
                      Decline
                    </StyledOfferButton>
                  </StyledOfferActions>
                </StyledOfferRow>
              </StyledListItem>
            );
          })
        )}
      </StyledList>
    </>
  );
};

export default function Development() {
  const { TRAINING_POPUP, FRIENDLY_POPUP } = POPUPS;
  const dispatch = useDispatch();
  const leaguesList = useSelector(isLeaguesListLoaded);
  const friendlyPopup = useSelector(getPopupByName(FRIENDLY_POPUP));
  const trainingPopup = useSelector(getPopupByName(TRAINING_POPUP));
  const friendlyTeamDirectory = useSelector(friendlyTeams);
  const trainingTeamDirectory = useSelector(trainingTeams);
  const homeData = useSelector(getInfoAboutDataHome);
  const myTeam = useSelector(getMyCurrentTeam);
  const strategyPracticeBoostUntil = useSelector(getPracticeBoostUntil);
  const myTeamId = myTeam?._id;

  const [friendly, setFriendly] = useState(null);
  const [training, setTraining] = useState(null);
  const [friendlyLoading, setFriendlyLoading] = useState(true);
  const [trainingLoading, setTrainingLoading] = useState(true);
  const [busyId, setBusyId] = useState(null);
  const [offerErrors, setOfferErrors] = useState({});
  const practiceBoostUntil =
    training?.practiceBoostUntil || strategyPracticeBoostUntil;

  const { Portal } = usePortal({
    bindTo: document && document.querySelector('#modal'),
  });

  const nameById = useMemo(
    () =>
      collectTeamNameMap(
        friendlyTeamDirectory,
        trainingTeamDirectory,
        leaguesList,
        homeData?.matchRunning,
        homeData?.next_match,
        homeData?.rankingTable,
        friendly,
        training
      ),
    [
      friendlyTeamDirectory,
      trainingTeamDirectory,
      leaguesList,
      homeData,
      friendly,
      training,
    ]
  );

  const friendlyOffers = useMemo(
    () => resolveOfferLabels(friendly?.offers, nameById),
    [friendly?.offers, nameById]
  );
  const trainingOffers = useMemo(
    () => resolveOfferLabels(training?.offers, nameById),
    [training?.offers, nameById]
  );

  const friendlyNext = friendly?.nextMatch || homeData?.next_match;
  const friendlyBlockReason = useMemo(
    () =>
      inviteAcceptBlockReason({
        kind: 'friendly',
        matchRunning: homeData?.matchRunning,
        nextMatch: friendlyNext,
        timezone: homeData?.timezone,
      }),
    [homeData, friendlyNext]
  );
  const trainingBlockReason = useMemo(
    () =>
      inviteAcceptBlockReason({
        kind: 'training',
        matchRunning: homeData?.matchRunning,
        nextMatch: homeData?.next_match,
        timezone: homeData?.timezone,
      }),
    [homeData]
  );

  const loadFriendly = useCallback(async () => {
    setFriendlyLoading(true);
    try {
      const response = await getFriendlyData();
      if (response.status === 200) {
        setFriendly(parseDevelopmentPayload(await response.json(), myTeamId));
      }
    } catch {
      /* Keep last friendly snapshot. */
    } finally {
      setFriendlyLoading(false);
    }
  }, [myTeamId]);

  const loadTraining = useCallback(async () => {
    setTrainingLoading(true);
    try {
      const response = await getTrainingData();
      if (response.status === 200) {
        setTraining(parseDevelopmentPayload(await response.json(), myTeamId));
      }
    } catch {
      /* Keep last training snapshot. */
    } finally {
      setTrainingLoading(false);
    }
  }, [myTeamId]);

  useEffect(() => {
    if (isEmpty(leaguesList)) {
      dispatch(getLeaguesList());
    }
  }, [dispatch, leaguesList]);

  useEffect(() => {
    if (isEmpty(homeData)) {
      dispatch(homeGetHomeInitialData());
    }
  }, [dispatch, homeData]);

  useEffect(() => {
    dispatch(getFriendlyList());
    dispatch(getTrainingList());
    loadFriendly();
    loadTraining();
  }, [dispatch, loadFriendly, loadTraining]);

  const friendlyWasOpen = React.useRef(false);
  const trainingWasOpen = React.useRef(false);
  useEffect(() => {
    const open = !isEmpty(friendlyPopup);
    if (friendlyWasOpen.current && !open) {
      loadFriendly();
    }
    friendlyWasOpen.current = open;
  }, [friendlyPopup, loadFriendly]);
  useEffect(() => {
    const open = !isEmpty(trainingPopup);
    if (trainingWasOpen.current && !open) {
      loadTraining();
    }
    trainingWasOpen.current = open;
  }, [trainingPopup, loadTraining]);

  const openFriendly = () => {
    dispatch(showPopupWindow(FRIENDLY_POPUP, FRIENDLY_POPUP, true));
    dispatch(getFriendlyList());
  };

  const openPractice = () => {
    dispatch(showPopupWindow(TRAINING_POPUP, TRAINING_POPUP, true));
    dispatch(getTrainingList());
  };

  const runOfferAction = async ({
    offerId,
    request,
    reload,
    successMessage,
    kind,
    action,
  }) => {
    setBusyId(offerId);
    try {
      const response = await request(offerId);
      if (response.status === 200) {
        setOfferErrors((prev) => {
          const next = { ...prev };
          delete next[offerId];
          return next;
        });
        toastRender('success', successMessage);
        await reload();
        dispatch(homeGetHomeInitialData());
      } else if (action === 'accept') {
        const message = acceptErrorMessage(kind, response.status);
        setOfferErrors((prev) => ({ ...prev, [offerId]: message }));
        toastRender('error', message);
      } else {
        toastRender('error', 'We could not update that offer.');
      }
    } catch {
      toastRender('error', 'We could not update that offer.');
    } finally {
      setBusyId(null);
    }
  };

  return (
    <StyledPage>
      {!isEmpty(trainingPopup) && (
        <Portal>
          <TrainingPopup popupName={TRAINING_POPUP} leaguesList={leaguesList} />
        </Portal>
      )}
      {!isEmpty(friendlyPopup) && (
        <Portal>
          <FriendlyPopup popupName={FRIENDLY_POPUP} leaguesList={leaguesList} />
        </Portal>
      )}

      <StyledColumn>
        <StyledSection>
          <h3>Friendly</h3>
          <StyledBody>
            <StyledOverview>
              <p>Invite another team to a friendly match.</p>
              <StyledActionButton type="button" onClick={openFriendly}>
                Send requests
              </StyledActionButton>
            </StyledOverview>
            <OffersBlock
              title="Sent / Received"
              offers={friendlyOffers}
              loading={friendlyLoading && !friendly}
              globalBlockReason={friendlyBlockReason}
              offerErrors={offerErrors}
              busyId={busyId}
              kind="friendly"
              matchRunning={homeData?.matchRunning}
              nextMatch={friendlyNext}
              timezone={homeData?.timezone}
              onAccept={(id) =>
                runOfferAction({
                  offerId: id,
                  request: acceptFriendlyOffer,
                  reload: loadFriendly,
                  successMessage: 'Friendly accepted.',
                  kind: 'friendly',
                  action: 'accept',
                })
              }
              onReject={(id) =>
                runOfferAction({
                  offerId: id,
                  request: rejectFriendlyOffer,
                  reload: loadFriendly,
                  successMessage: 'Friendly declined.',
                  kind: 'friendly',
                  action: 'reject',
                })
              }
              onCancel={(id) =>
                runOfferAction({
                  offerId: id,
                  request: cancelFriendlyOffer,
                  reload: loadFriendly,
                  successMessage: 'Friendly cancelled.',
                  kind: 'friendly',
                  action: 'cancel',
                })
              }
            />
            <GoalsBlock
              daily={friendly?.dailyTasks}
              season={friendly?.seasonTasks}
              loading={friendlyLoading && !friendly}
            />
            <PastMatchesBlock
              title="Last friendlies"
              pastMatches={friendly?.pastMatches}
              loading={friendlyLoading && !friendly}
            />
          </StyledBody>
        </StyledSection>
      </StyledColumn>

      <StyledColumn>
        <StyledSection>
          <h3>Practice</h3>
          <StyledBody>
            <StyledOverview>
              <p>Invite another team to a practice session.</p>
              <p>
                <strong>Practice boost</strong>
                {practiceBoostUntil
                  ? ` — active until ${new Date(practiceBoostUntil).toLocaleString(
                      undefined,
                      {
                        weekday: 'short',
                        hour: '2-digit',
                        minute: '2-digit',
                      }
                    )}.`
                  : ' — none active.'}
              </p>
              <StyledActionButton type="button" onClick={openPractice}>
                Send requests
              </StyledActionButton>
            </StyledOverview>
            <OffersBlock
              title="Sent / Received"
              offers={trainingOffers}
              loading={trainingLoading && !training}
              globalBlockReason={trainingBlockReason}
              offerErrors={offerErrors}
              busyId={busyId}
              kind="training"
              matchRunning={homeData?.matchRunning}
              nextMatch={homeData?.next_match}
              timezone={homeData?.timezone}
              onAccept={(id) =>
                runOfferAction({
                  offerId: id,
                  request: acceptTrainingOffer,
                  reload: loadTraining,
                  successMessage: 'Practice accepted.',
                  kind: 'training',
                  action: 'accept',
                })
              }
              onReject={(id) =>
                runOfferAction({
                  offerId: id,
                  request: rejectTrainingOffer,
                  reload: loadTraining,
                  successMessage: 'Practice declined.',
                  kind: 'training',
                  action: 'reject',
                })
              }
              onCancel={(id) =>
                runOfferAction({
                  offerId: id,
                  request: cancelTrainingOffer,
                  reload: loadTraining,
                  successMessage: 'Practice cancelled.',
                  kind: 'training',
                  action: 'cancel',
                })
              }
            />
            <GoalsBlock
              daily={training?.dailyTasks}
              season={training?.seasonTasks}
              loading={trainingLoading && !training}
            />
            <PastMatchesBlock
              title="Last practices"
              pastMatches={training?.pastMatches}
              loading={trainingLoading && !training}
            />
          </StyledBody>
        </StyledSection>
      </StyledColumn>
    </StyledPage>
  );
}
